import React, { useMemo, useState, useEffect } from 'react';
import WorkflowEditor from './components/editors/WorkflowEditor';
import WorkflowSimulator from './components/editors/WorkflowSimulator';
import { EditorProvider } from './contexts/EditorContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NODE_TYPES_CONFIG, NODE_CATEGORIES } from './constants';
import { PROPERTY_REGISTRY } from './utils/propertyPanelRegistry';
import { defaultComponentRegistry } from './utils/componentRegistry';
import { directoryService } from './services/mockDirectoryService';
import { useWorkflowEditor } from './hooks/useWorkflowEditor';
import { useWorkflowSimulation } from './hooks/useWorkflowSimulation';
import { ProcessDefinition, AIService, WorkflowProject } from './types';
import { AutoSavePayload } from './hooks/useAutoSave';

export interface ControllerProps {
  initialData?: ProcessDefinition[];
  onBack?: () => void;
  onSave?: (data: WorkflowProject) => Promise<void>;
  onSaveAndQuit?: (data: WorkflowProject) => Promise<void>;
  onAutoSave?: (data: AutoSavePayload) => void;
  isLoading?: boolean;
  customComponents?: Record<string, React.ComponentType<any>>;
  aiService?: AIService;
  theme?: "light" | "dark";
}

const ControllerContent: React.FC<Omit<ControllerProps, 'theme'>> = ({ 
  initialData, 
  onBack, 
  onSave, 
  onSaveAndQuit, 
  onAutoSave,
  isLoading: externalLoading = false,
  customComponents = {},
  aiService
}) => {
  const [fallbackAi, setFallbackAi] = useState<AIService | null>(null);

  useEffect(() => {
    if (!aiService && !fallbackAi) {
      import('./services/geminiService').then(m => {
        setFallbackAi(m.geminiService);
      });
    }
  }, [aiService, fallbackAi]);

  const editor = useWorkflowEditor({
    initialData,
    onAutoSave
  });

  const simulator = useWorkflowSimulation({
      nodes: editor.nodes,
      setNodes: editor.setNodes,
      edges: editor.edges,
      fitView: editor.fitView,
      focusNode: editor.focusNode
  });

  const config = useMemo(() => ({
    nodeTypes: NODE_TYPES_CONFIG,
    nodeCategories: NODE_CATEGORIES,
    propertyRegistry: PROPERTY_REGISTRY,
    componentRegistry: { ...defaultComponentRegistry, ...customComponents },
    services: {
      ai: aiService || fallbackAi || {
          generateWorkflowCompletion: async () => "Initializing AI...",
          evaluateCondition: async () => true
      },
      directory: directoryService
    },
    actions: {
        openModal: editor.openModalAtTab
    }
  }), [customComponents, editor.openModalAtTab, aiService, fallbackAi]);

  const onNodeClick = simulator.simulationState.isRunning ? undefined : editor.onNodeClick;
  const onEdgeClick = simulator.simulationState.isRunning ? undefined : editor.onEdgeClick;
  const onPaneClick = simulator.simulationState.isRunning ? undefined : editor.onPaneClick;
  const onNodeDoubleClick = simulator.simulationState.isRunning ? undefined : editor.onNodeDoubleClick;
  const onNodeContextMenu = simulator.simulationState.isRunning ? undefined : editor.onNodeContextMenu;

  const handleExternalSave = async () => {
      if (onSave) {
          editor.setIsLoading(true);
          try {
              await onSave(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleSaveAndQuitClick = async () => {
      if (onSaveAndQuit) {
          editor.setIsLoading(true);
          try {
              await onSaveAndQuit(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleExport = () => {
      editor.handleSaveProject();
  };

  const isGlobalLoading = editor.isLoading || externalLoading;

  return (
    <EditorProvider config={config}>
      {simulator.simulationState.isRunning ? (
        <WorkflowSimulator
          nodes={editor.nodes}
          edges={editor.edges}
          
          simulationState={simulator.simulationState}
          onToggleSimulation={simulator.stopSimulation}
          onResetSimulation={simulator.resetSimulation}
          onSimulationAction={simulator.handleSimulationAction}
          suggestedPath={simulator.suggestedPath}
          
          activeProcessId={editor.activeProcessId}
          processes={editor.processes}
          onSelectProcess={editor.handleSelectProcess}
          onCreateProcess={editor.handleCreateProcess}
          onDeleteProcess={editor.handleDeleteProcess}
          onSaveProject={handleExport}
          onLoadProject={editor.handleLoadProject}
          onBack={onBack}
          onSave={onSave ? handleExternalSave : undefined}
          onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}
        />
      ) : (
        <WorkflowEditor
            nodes={editor.nodes}
            edges={editor.edges}
            onNodesChange={editor.onNodesChange}
            onEdgesChange={editor.onEdgesChange}
            onConnect={editor.onConnect}
            onConnectStart={editor.onConnectStart}
            onConnectEnd={editor.onConnectEnd}
            onDrop={editor.onDrop}
            onDragOver={editor.onDragOver}
            
            simulationState={simulator.simulationState}
            onToggleSimulation={simulator.startSimulation}
            onResetSimulation={simulator.resetSimulation}
            
            activeProcessId={editor.activeProcessId}
            processes={editor.processes}
            onSelectProcess={editor.handleSelectProcess}
            onCreateProcess={editor.handleCreateProcess}
            onDeleteProcess={editor.handleDeleteProcess}
            onUpdateProcess={editor.handleUpdateProcess}
            
            onSaveProject={handleExport}
            onLoadProject={editor.handleLoadProject}
            onAutoLayout={editor.handleAutoLayout}
            
            onBack={onBack}
            onSave={onSave ? handleExternalSave : undefined}
            onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}

            onNodeClick={onNodeClick!}
            onEdgeClick={onEdgeClick!}
            onNodeDoubleClick={onNodeDoubleClick!}
            onNodeContextMenu={onNodeContextMenu!}
            onPaneClick={onPaneClick!}
            selectedNode={editor.selectedNode}
            selectedEdge={editor.selectedEdge}
            updateNodeData={editor.updateNodeData}
            updateEdgeData={editor.updateEdgeData}
            isModalOpen={editor.isModalOpen}
            setIsModalOpen={editor.setIsModalOpen}
            activeTabId={editor.activeTabId}
            setActiveTabId={editor.setActiveTabId}
            isDeployModalOpen={editor.isDeployModalOpen}
            setIsDeployModalOpen={editor.setIsDeployModalOpen}
            
            isLoading={isGlobalLoading}
            autoSaveState={editor.autoSaveState}
            variableContext={editor.variableContext}
        />
      )}
    </EditorProvider>
  );
};

const Controller: React.FC<ControllerProps> = ({ theme, ...rest }) => {
    return (
        <ThemeProvider forcedTheme={theme}>
            <ControllerContent {...rest} />
        </ThemeProvider>
    );
};

export default Controller;